import React, { useRef, useLayoutEffect, TextareaHTMLAttributes } from 'react';
import { cn } from '@lightning/utils';
import { Textarea } from './textarea';

export interface AutoResizeTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  minRows?: number;
  maxRows?: number;
  lineHeight?: string | number;
}

const AutoResizeTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  ({ className, minRows = 1, maxRows = 5, lineHeight = '1.5', onChange, style, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Merge the forwarded ref with our local ref
    const assignRef = (element: HTMLTextAreaElement) => {
      textareaRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }
    };

    // Adjust height of textarea based on content
    useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Temporarily zero out height and min-height to get true content scrollHeight
      textarea.style.height = '0';
      textarea.style.minHeight = '0';

      // Calculate the line height based on font size
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight) || parseInt(computedStyle.fontSize) * 1.2;

      // Calculate min and max heights
      const minHeight = minRows * lineHeight;
      const maxHeight = maxRows * lineHeight;

      // Set the height based on scrollHeight, constrained between min and max
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(minHeight, scrollHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
      textarea.style.minHeight = '';

      // Set overflow if content exceeds max height
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [props.value, minRows, maxRows]);

    return (
      <Textarea
        ref={assignRef}
        className={cn(
          // Height must reset synchronously for scrollHeight to measure the new
          // content, not an intermediate animated height after clearing input.
          "resize-none overflow-hidden py-2.5",
          "text-base min-h-0", // min-h-0 overrides Textarea's min-h-[80px]; consumers can set their own min-h
          className
        )}
        onChange={handleChange}
        rows={minRows}
        style={{
          lineHeight,
          ...style,
          fontSize: '16px', // Must stay last to prevent iOS auto-zoom on focus
        }}
        {...props}
      />
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';

export { AutoResizeTextarea };
